// Container loading (Logistics → Shipment Control → a shipment's "Container loading" link): a 3D view of a 20ft, 40ft
// or 40ft High Cube container with the shipment's cartons stacked inside, one colour per PO, and how full it is (by
// volume, cartons, space and floor left). Cartons come from the packing list, or the shipment quantity ÷ pieces per
// carton; carton sizes from the shipment, the garment's Packing tab or a standard 60 × 40 × 40 cm carton. People can
// try other numbers here; nothing is saved except the loading choice (Max fill, the default, or Keep POs in blocks),
// which is saved per shipment. Fragile cartons never have a non-fragile carton on top. The 3D drawing (web/src/container3d.ts) loads only on this screen.
import { esc } from "../lib.js";
import { get, put, toast } from "../lib.js";
import { onAction, route } from "../state.js";
import { backBar, errorPanel, loadingPanel, renderShell, setContent } from "../shell.js";
import { DEFAULT_LOAD_MODE, LOAD_MODES, cartonCbm, containerCbm, insideCm, lineCartons, loadSummary, poColour, type LoadLine, type LoadMode, type LoadWall } from "../../../shared/containerload.js";
import type { ContainerType } from "../../../shared/orders.js";
import type { Scene3D, View } from "../container3d.js";

let d: any = null;
let lines: LoadLine[] = [];
let uid = "";
let hl: number | null = null;
let scene: Scene3D | null = null;
let view: View = "3d";
let mode: LoadMode = DEFAULT_LOAD_MODE;
/** PO lines opened to show their carton boxes (the list is folded to one row per line by default). */
let open = new Set<number>();

const n = (v: number, dp = 0) => Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
const containers = (): ContainerType[] => d?.containers || [];
const chosen = () => containers().find((c) => c.uid === uid) || containers()[0];
const poIdx = (poId: string) => (d.pos as Array<{ poId: string }>).findIndex((p) => p.poId === poId);
const SIZE_NOTE: Record<string, string> = {
  shipment: "Carton size from this shipment's landed cost packing",
  product: "Carton size from the garment's Packing tab",
  standard: "No carton size saved: a standard export carton (60 × 40 × 40 cm) is used",
};

/** Load the 3D drawing (its own file, built next to app.js with the same version). */
async function load3d(): Promise<typeof import("../container3d.js")> {
  const url = `/container3d.js${new URL(import.meta.url).search}`;
  return import(/* @vite-ignore */ url);
}

route("/app/shipments/:id/loading", async ({ params }) => {
  scene?.dispose();
  scene = null;
  renderShell({ active: "shipmentcontrol", title: "Container loading", subtitle: "", content: loadingPanel() });
  try { d = await get(`/api/org/shipments/${encodeURIComponent(params.id)}/loading`); } catch (err) { return setContent(`${backBar("/app/shipments", "Back to Shipment Control")}${errorPanel((err as Error).message)}`); }
  lines = d.lines.map((l: LoadLine) => ({ ...l }));
  uid = d.planned || containers()[0]?.uid || "";
  hl = null;
  // Lines that can't be loaded yet (no pieces per carton) start open, so the box to fill in is in view.
  open = new Set(lines.map((l, i) => (lineCartons(l).from === "none" ? i : -1)).filter((i) => i >= 0));
  view = "3d";
  mode = d.mode === "blocks" ? "blocks" : "max";
  const sh = d.shipment;
  renderShell({ active: "shipmentcontrol", title: `Container loading · ${sh.label}`, subtitle: [sh.route, sh.statusLabel, sh.container ? `Container ${sh.container}` : ""].filter(Boolean).join(" · "), content: "" });
  const notes = [
    d.hiddenPos ? `This shipment also carries ${d.hiddenPos} PO${d.hiddenPos === 1 ? "" : "s"} you can't see. ${d.hiddenPos === 1 ? "It isn't" : "They aren't"} shown, so the container may be fuller than this.` : "",
    ...d.skipped.map((s: any) => `${esc(s.poNumber)} · ${esc(s.code)}: ${esc(s.why)}`),
  ].filter(Boolean);
  setContent(`${backBar("/app/shipments", "Back to Shipment Control")}
<div class="cl-page" id="cl-page">
  <div class="cl-note">Cartons come from each PO's packing list, or the quantity on this shipment ÷ pieces per carton. Colours show each PO on this screen only.
  Tap a PO below to see its cartons and try other numbers; they aren't saved. To change them for good, edit the garment's Packing tab or the packing list.</div>
  ${notes.length ? `<div class="cl-note cl-note-plain">${notes.map((x) => `<div>${x}</div>`).join("")}</div>` : ""}
  ${!lines.length ? '<div class="panel"><div class="empty">This shipment has no garments or cartons to load yet.</div></div>' : `
  <div class="cl-grid">
    <div class="cl-left">
      <section class="cl-card cl-container-card"><header><h3>Container</h3><small id="cl-inside"></small></header>
        <div class="cl-body"><div class="cl-seg" id="cl-types" role="group" aria-label="Container size"></div>${d.booked ? `<small class="cl-hint">Freight booking: ${esc(d.booked)}</small>` : ""}</div></section>
      <section class="cl-card cl-lines-card"><header><h3>Purchase orders on this shipment</h3><small>${d.pos.length} PO${d.pos.length === 1 ? "" : "s"}</small><button type="button" class="cl-openall" data-action="cl-openall" id="cl-openall">Open all</button></header>
        <div class="cl-po-head" aria-hidden="true"><span>PO · garment</span><span class="cl-qty">On shipment</span><span>Cartons</span><span>CBM</span><span></span></div>
        <div class="cl-rows" id="cl-lines"></div>
        <div class="cl-po-total" id="cl-total"></div>
        <div class="cl-body cl-reset-row"><button type="button" class="secondary" data-action="cl-reset" id="cl-reset" hidden>Back to the saved numbers</button></div></section>
    </div>
    <div class="cl-right">
      <section class="cl-card"><header><h3>How full is the container</h3><small id="cl-title"></small></header>
        <div class="cl-body cl-mode-row"><div class="cl-seg cl-mode" id="cl-mode" role="group" aria-label="How the cartons are arranged">${LOAD_MODES.map(([k, label]) => `<button type="button" data-action="cl-mode" data-mode="${k}" aria-pressed="${k === mode}">${esc(label)}${k === "max" ? "<small>Default</small>" : ""}</button>`).join("")}</div>
          <small class="cl-hint" id="cl-mode-note"></small></div>
        <div class="cl-stats">
          <div><span>Filled</span><strong id="cl-fill">0%</strong><small>by volume</small></div>
          <div><span>Cartons loaded</span><strong id="cl-ctn">0</strong><small id="cl-ctn2"></small></div>
          <div><span>Space left</span><strong id="cl-left">0</strong><small>CBM (m³)</small></div>
          <div><span>Floor left</span><strong id="cl-floor">0</strong><small>metres to the doors</small></div>
        </div>
        <div class="cl-gauge"><div class="cl-bar" id="cl-bar"></div><div class="cl-gl"><span id="cl-used"></span><span id="cl-practical"></span></div><div class="cl-status" id="cl-status" role="status"></div></div>
        <div class="cl-viewer" id="cl-viewer">
          <canvas id="cl-canvas" aria-label="3D view of the container with the cartons stacked inside"></canvas>
          <div class="cl-tools">${(["3d", "side", "top", "door"] as View[]).map((v) => `<button type="button" class="secondary" data-action="cl-view" data-view="${v}" aria-pressed="${v === "3d"}">${{ "3d": "3D", side: "Side", top: "Top", door: "Door end" }[v]}</button>`).join("")}
            <button type="button" class="secondary" data-action="cl-spin" id="cl-spin" aria-pressed="false">Turn slowly</button></div>
          <div class="cl-hint-3d">Drag to turn · scroll or pinch to zoom</div>
          <div class="cl-no3d" id="cl-no3d" hidden>The 3D view can't be shown on this device. The numbers above are still right.</div>
        </div>
        <div class="cl-legend" id="cl-legend"></div>
        <p class="cl-foot" id="cl-foot"></p>
        <div class="cl-walls" id="cl-walls"></div>
      </section>
    </div>
  </div>`}
</div>`);
  if (!lines.length) return;
  const page = document.getElementById("cl-page")!;
  page.addEventListener("input", (e) => {
    const el = e.target as HTMLInputElement;
    if (!el.dataset.k) return;
    const l = lines[Number(el.dataset.i)];
    if (el.dataset.k === "fragile") l.fragile = el.checked;
    else if (el.dataset.k === "maxOnTop") { el.value = el.value.replace(/\D/g, "").slice(0, 2); l.maxOnTop = Number(el.value || 0); }
    else (l as any)[el.dataset.k] = Math.max(0, Number(el.value) || 0);
    lineCalc(Number(el.dataset.i));
    update();
  });
  drawTypes();
  drawLines();
  update();
  try {
    const m = await load3d();
    const canvas = document.getElementById("cl-canvas") as HTMLCanvasElement | null;
    if (!canvas) return;
    scene = m.mount(canvas);
    draw3d();
  } catch {
    document.getElementById("cl-no3d")!.hidden = false;
    document.getElementById("cl-canvas")?.remove();
  }
});

function drawTypes() {
  const box = document.getElementById("cl-types");
  if (!box) return;
  box.innerHTML = containers().map((c) => `<button type="button" data-action="cl-type" data-uid="${esc(c.uid)}" aria-pressed="${c.uid === uid}">${esc(c.name)}<small>${n(containerCbm(c), 1)} CBM</small></button>`).join("");
  const c = chosen();
  if (c) document.getElementById("cl-inside")!.textContent = `Inside ${n(c.internalLengthM, 2)} × ${n(c.internalWidthM, 2)} × ${n(c.internalHeightM, 2)} m`;
}

function calcHtml(l: LoadLine): string {
  const c = lineCartons(l), each = cartonCbm(l);
  const sum = c.from === "packing" ? `Packing list: <b>${n(c.cartons)} cartons</b>` : c.from === "estimate" ? `${n(l.qty)} ${esc(l.unit)} ÷ ${n(l.pcsPerCarton)} per carton = <b>${n(c.cartons)} cartons</b>`
    : `<span class="cl-warn">Enter pieces per carton to load this line.</span>`;
  return `<span>${sum}</span><span>${n(each, 3)} CBM each · ${n(c.cartons * each, 2)} CBM total</span>${c.partLast ? `<span class="cl-warn">Last carton has ${n(c.partLast)} pcs (part carton)</span>` : ""}`;
}
/** The folded row: PO, garment, tags, quantity, cartons and CBM. */
function rowHtml(l: LoadLine, i: number): string {
  const c = lineCartons(l), cbm = c.cartons * cartonCbm(l);
  const tags = [
    c.from === "none" ? '<em class="cl-tag warn">Needs pieces per carton</em>' : "",
    l.sizeFrom === "standard" ? '<em class="cl-tag warn">Standard carton</em>' : "",
    l.fragile ? '<em class="cl-tag frag">Fragile</em>' : "",
    l.maxOnTop ? `<em class="cl-tag info">Max ${n(l.maxOnTop)} on top</em>` : "",
    c.partLast ? '<em class="cl-tag warn">Part carton</em>' : "",
  ].join("");
  const qty = `${n(l.qty)} ${esc(l.unit)}`;
  return `<span class="cl-po"><b>${esc(l.poNumber)}<i class="cl-qty-inline">${qty}</i></b><small>${esc(l.code)} · ${esc(l.description)}${l.colour ? ` · ${esc(l.colour)}` : ""}</small>${tags ? `<span class="cl-tags">${tags}</span>` : ""}</span>
  <span class="cl-num cl-qty">${qty}</span><span class="cl-num cl-ctn">${c.from === "none" ? "–" : n(c.cartons)}</span><span class="cl-num">${c.from === "none" ? "–" : n(cbm, 2)}</span>
  <button type="button" class="cl-chev" data-action="cl-line" data-i="${i}" aria-expanded="${open.has(i)}" aria-label="Carton details for ${esc(l.poNumber)} ${esc(l.code)}">▾</button>`;
}
function drawTotal() {
  const el = document.getElementById("cl-total");
  if (!el) return;
  const t = lines.reduce((a, l) => { const c = lineCartons(l); a.ctn += c.cartons; a.cbm += c.cartons * cartonCbm(l); return a; }, { ctn: 0, cbm: 0 });
  el.innerHTML = `<span>Total</span><span class="cl-qty"></span><span class="cl-num">${n(t.ctn)}</span><span class="cl-num">${n(t.cbm, 2)}</span><span></span>`;
  const all = document.getElementById("cl-openall");
  if (all) all.textContent = open.size === lines.length ? "Close all" : "Open all";
}
function lineCalc(i: number) {
  const el = document.querySelector(`#cl-lines [data-line="${i}"] .cl-calc`);
  if (el) el.innerHTML = calcHtml(lines[i]);
  const top = document.querySelector(`#cl-lines [data-line="${i}"] .cl-line-top`);
  if (top) top.innerHTML = rowHtml(lines[i], i);
  drawTotal();
  const changed = lines.some((l, k) => (["pcsPerCarton", "l", "w", "h"] as const).some((f) => l[f] !== d.lines[k][f]) || !!l.fragile !== !!d.lines[k].fragile || (l.maxOnTop || 0) !== (d.lines[k].maxOnTop || 0));
  document.getElementById("cl-reset")!.hidden = !changed;
}
function drawLines() {
  const box = document.getElementById("cl-lines");
  if (!box) return;
  box.innerHTML = lines.map((l, i) => {
    const fromPacking = lineCartons(l).from === "packing";
    const num = (k: "pcsPerCarton" | "l" | "w" | "h", label: string, extra = "") => `<input id="cl-${k}-${i}" type="number" min="${k === "pcsPerCarton" ? 1 : 1}" step="${k === "pcsPerCarton" ? 1 : 0.1}" value="${l[k] || ""}" data-k="${k}" data-i="${i}" aria-label="${esc(label)}" ${extra}>`;
    return `<div class="cl-line${hl === poIdx(l.poId) ? " hl" : ""}${open.has(i) ? " open" : ""}" style="--c:${poColour(poIdx(l.poId))}" data-line="${i}" data-po="${poIdx(l.poId)}">
  <div class="cl-line-top" data-action="cl-line" data-i="${i}">${rowHtml(l, i)}</div>
  <div class="cl-edit">
  <div class="cl-fields">
    <label>On this shipment<output>${n(l.qty)} ${esc(l.unit)}</output></label>
    <label for="cl-pcsPerCarton-${i}">Pieces per carton${num("pcsPerCarton", "Pieces per carton", fromPacking ? 'disabled title="The packing list gives the number of cartons"' : "")}</label>
    <label class="cl-dims">Carton size L × W × H (cm)<span>${num("l", "Carton length cm")}<em>×</em>${num("w", "Carton width cm")}<em>×</em>${num("h", "Carton height cm")}</span></label>
  </div>
  <div class="cl-stack">
    <label class="cb-row"><input type="checkbox" id="cl-fragile-${i}" data-k="fragile" data-i="${i}" ${l.fragile ? "checked" : ""}><span>Fragile: only fragile cartons on top</span></label>
    <label for="cl-maxOnTop-${i}">Max cartons on top<input id="cl-maxOnTop-${i}" type="text" inputmode="numeric" maxlength="2" value="${l.maxOnTop || ""}" placeholder="No limit" data-k="maxOnTop" data-i="${i}"></label>
  </div>
  <div class="cl-calc">${calcHtml(l)}</div>
  <small class="cl-src${l.sizeFrom === "standard" ? " cl-warn" : ""}">${esc(SIZE_NOTE[l.sizeFrom])}</small>
  </div>
</div>`;
  }).join("");
  drawTotal();
}

function update() {
  const c = chosen();
  if (!c) return;
  const s = loadSummary(c, lines, containers(), mode);
  const set = (id: string, v: string) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  set("cl-title", `${c.name} container`);
  set("cl-fill", `${n(s.fillPct)}%`);
  set("cl-ctn", n(s.loadedCartons));
  set("cl-ctn2", `of ${n(s.totalCartons)} on this shipment`);
  set("cl-left", n(s.leftCbm, 1));
  set("cl-floor", n(s.floorLeftM, 2));
  set("cl-used", `${n(s.usedCbm, 1)} of ${n(s.capacityCbm, 1)} CBM used${s.weightKnown ? ` · ${n(s.grossKg)} kg gross${c.maxPayloadKg ? ` of ${n(c.maxPayloadKg)} kg` : ""}` : ""}`);
  set("cl-practical", `Dashed line = practically full (${n(s.practicalCbm, 1)} CBM: cartons never fill every gap)`);
  const perPo = (d.pos as any[]).map((p, i) => ({ ...p, i, cbm: s.per.reduce((a, x, k) => a + (lines[k].poId === p.poId ? x.cbm : 0), 0), ctn: s.per.reduce((a, x, k) => a + (lines[k].poId === p.poId ? x.loaded : 0), 0) }));
  const bar = document.getElementById("cl-bar")!;
  bar.innerHTML = perPo.map((p) => `<i style="--c:${poColour(p.i)};width:${s.capacityCbm ? (p.cbm / s.capacityCbm) * 100 : 0}%" title="${esc(p.poNumber)}: ${n(p.cbm, 2)} CBM"></i>`).join("")
    + `<span class="cl-mark" style="left:${Math.min(100, s.practicalPct)}%" title="Practically full"></span>`;
  const st = document.getElementById("cl-status")!;
  const noSize = s.per.filter((p) => !p.fits).length;
  if (s.notLoaded > 0) {
    st.className = "cl-status bad";
    st.textContent = `${n(s.notLoaded)} carton${s.notLoaded === 1 ? "" : "s"} don't fit in a ${c.name}.${noSize ? " A carton is bigger than the container." : s.bigger ? ` Everything fits in a ${s.bigger.name}.` : " Split this shipment over two containers."}`;
  } else if (s.overWeight) {
    st.className = "cl-status bad";
    st.textContent = `All ${n(s.totalCartons)} cartons fit by size, but they weigh ${n(s.grossKg)} kg: more than the ${c.name}'s ${n(c.maxPayloadKg)} kg limit.`;
  } else if (s.usedCbm > s.practicalCbm) {
    st.className = "cl-status warn";
    st.textContent = `All ${n(s.totalCartons)} cartons fit in this drawing, but it's past practically full. Expect it to be tight when loading.`;
  } else {
    st.className = "cl-status ok";
    const top = s.topUp ? lines[s.topUp.line] : null;
    st.textContent = `All ${n(s.totalCartons)} cartons fit. Room for about ${n(s.roomBeforeFull, 1)} CBM more before it's practically full.`
      + (s.topUp && top ? ` Top-up: about ${n(s.topUp.cartons)} more carton${s.topUp.cartons === 1 ? "" : "s"} of ${top.poNumber} · ${top.code} would still fit.` : "");
  }
  modeNote(s);
  drawWalls(s.walls);
  document.getElementById("cl-legend")!.innerHTML = perPo.map((p) => `<button type="button" class="cl-chip${hl === p.i ? " hl" : ""}" style="--c:${poColour(p.i)}" data-action="cl-hl" data-po="${p.i}"><span class="cl-sw"></span>${esc(p.poNumber)} · ${n(p.ctn)} ctns · ${n(p.cbm, 1)} CBM</button>`).join("");
  const v = document.getElementById("cl-viewer");
  if (v) { v.dataset.container = c.uid; v.dataset.cartons = String(s.loadedCartons); }
  draw3d(s);
}

/** Under the Max fill / Keep POs in blocks buttons: what the other choice would leave. */
function modeNote(s: ReturnType<typeof loadSummary>) {
  const el = document.getElementById("cl-mode-note");
  const foot = document.getElementById("cl-foot");
  if (foot) foot.textContent = mode === "max"
    ? `Max fill: each PO is loaded from the back wall to the doors, and the next PO fills any room a PO leaves in its last walls, so no space is wasted. ${s.sharedWalls ? `${n(s.sharedWalls)} wall${s.sharedWalls === 1 ? " is" : "s are"} shared by two POs (listed below).` : "No walls are shared."} Fragile cartons only ever have fragile cartons on top. Cartons stay "this way up".`
    : `Keep POs in blocks: loaded PO by PO from the back wall to the doors, each PO in its own walls, so each PO comes off together. Part-full walls are left part-empty. Cartons stay "this way up".`;
  if (!el) return;
  const diff = mode === "max" ? s.floorLeftM - s.other.floorLeftM : s.other.floorLeftM - s.floorLeftM;
  const moreLoaded = mode === "max" ? s.loadedCartons - s.other.loadedCartons : s.other.loadedCartons - s.loadedCartons;
  const gain = moreLoaded > 0 ? `${n(moreLoaded)} more carton${moreLoaded === 1 ? "" : "s"} fit` : diff > 0.004 ? `${n(diff, 2)} m more floor is left` : "";
  el.textContent = gain ? (mode === "max" ? `Compared with keeping POs in blocks: ${gain}.` : `With Max fill, ${gain}.`) : mode === "max" ? "Same space as keeping POs in blocks for this load." : "Max fill would use the same space for this load.";
  if (!d.canSaveMode) el.textContent += " Only people who can edit this shipment can save the choice.";
}

/** The loading list: walls from the back wall to the doors, with the walls shared by two POs marked. */
function drawWalls(walls: LoadWall[]) {
  const box = document.getElementById("cl-walls");
  if (!box) return;
  const poName = (i: number) => (d.pos as any[])[i]?.poNumber || "";
  const groups: Array<{ from: number; to: number; w: LoadWall; cartons: number; fragile: number }> = [];
  for (const w of walls) {
    const g = groups[groups.length - 1];
    if (g && !w.shared && !g.w.shared && g.w.pos[0] === w.pos[0] && g.to === w.n - 1) { g.to = w.n; g.cartons += w.cartons[0] || 0; g.fragile += w.fragile; }
    else groups.push({ from: w.n, to: w.n, w, cartons: w.cartons.reduce((a, b) => a + b, 0), fragile: w.fragile });
  }
  box.innerHTML = walls.length ? `<h4>Loading list <small>back wall to doors</small></h4><ol>${groups.map((g) => {
    const name = g.from === g.to ? `Wall ${g.from}` : `Walls ${g.from}–${g.to}`;
    const what = g.w.shared ? g.w.pos.map((p, k) => `<span class="cl-sw" style="--c:${poColour(p)}"></span>${esc(poName(p))} (${n(g.w.cartons[k])})`).join(" + ")
      : `<span class="cl-sw" style="--c:${poColour(g.w.pos[0])}"></span>${esc(poName(g.w.pos[0]))} · ${n(g.cartons)} ctns`;
    return `<li class="${g.w.shared ? "shared" : ""}"><b>${name}</b><span>${what}</span>${g.w.shared ? '<em class="cl-tag">Shared</em>' : ""}${g.fragile ? `<em class="cl-tag frag">${n(g.fragile)} fragile</em>` : ""}</li>`;
  }).join("")}</ol>` : "";
}

function draw3d(s = chosen() ? loadSummary(chosen()!, lines, containers(), mode) : null) {
  const c = chosen();
  if (!scene || !c || !s) return;
  const { L, W, H } = insideCm(c);
  scene.draw({ L, W, H, boxes: s.boxes, colours: (d.pos as any[]).map((_, i) => poColour(i)), dark: document.body.classList.contains("dark-theme"), labels: { doors: "Doors", back: "Back wall" } });
  scene.view(view);
  scene.highlight(hl);
}

function setHighlight(po: number | null) {
  hl = po;
  document.querySelectorAll<HTMLElement>("#cl-page .cl-line, #cl-page .cl-chip").forEach((el) => el.classList.toggle("hl", el.dataset.po === String(po)));
  scene?.highlight(po);
}

onAction("cl-type", (el) => { uid = el.dataset.uid!; drawTypes(); update(); });
onAction("cl-mode", async (el) => {
  const next = el.dataset.mode === "blocks" ? "blocks" : "max";
  if (next === mode) return;
  const was = mode;
  mode = next;
  document.querySelectorAll<HTMLElement>('[data-action="cl-mode"]').forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.mode === mode)));
  update();
  if (!d?.canSaveMode) return;
  try { await put(`/api/org/shipments/${encodeURIComponent(d.shipment.id)}/loading/mode`, { mode }); d.mode = mode; toast(mode === "max" ? "Saved: Max fill" : "Saved: Keep POs in blocks"); }
  catch (err) { toast((err as Error).message, "error"); mode = was; document.querySelectorAll<HTMLElement>('[data-action="cl-mode"]').forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.mode === mode))); update(); }
});
onAction("cl-view", (el) => {
  view = el.dataset.view as View;
  document.querySelectorAll<HTMLElement>('[data-action="cl-view"]').forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.view === view)));
  scene?.view(view);
});
onAction("cl-spin", (el) => { const on = el.getAttribute("aria-pressed") !== "true"; el.setAttribute("aria-pressed", String(on)); scene?.spin(on); });
onAction("cl-hl", (el) => { const po = Number(el.dataset.po); setHighlight(hl === po ? null : po); });
// A row opens to show its carton boxes and lights up its PO in the 3D view; tapping it again folds it.
onAction("cl-line", (el) => {
  const i = Number(el.dataset.i), row = document.querySelector<HTMLElement>(`#cl-lines [data-line="${i}"]`);
  if (!row) return;
  const on = !open.has(i);
  if (on) open.add(i); else open.delete(i);
  row.classList.toggle("open", on);
  row.querySelector(".cl-chev")?.setAttribute("aria-expanded", String(on));
  const po = Number(row.dataset.po);
  if (on || hl === po) setHighlight(on ? po : null);
  drawTotal();
});
onAction("cl-openall", () => { open = open.size === lines.length ? new Set() : new Set(lines.map((_, i) => i)); drawLines(); });
onAction("cl-reset", () => { lines = d.lines.map((l: LoadLine) => ({ ...l })); drawLines(); lineCalc(0); update(); });
